// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — one dataset compared with another (research tier, owner
 * 2026-10-06): the dataset on the globe (A) minus a real-time overlay that
 * carries values (B), frame by frame, the way papers map a model's bias
 * against observations or one scenario against another.
 *
 * What makes the difference mean something, each a function here:
 * - **the same place:** both are averaged onto one grid as coarse as the
 *   coarser of the two (`compareFields`), so the finer dataset's detail does
 *   not appear as difference;
 * - **the same units:** B is converted into A's units only through a known
 *   conversion (`unitConversion`), never guessed; a change or anomaly takes
 *   the factor without the offset (a 1 °F change is 0.56 °C, not −17 °C);
 * - **the same moment:** the two frames' dates must lie within half the
 *   coarser time step (`pairFrames`);
 * - **the same quantity:** same units do not make the same variable (cloud
 *   cover and sea ice are both %), so `quantityWarning` says when the titles
 *   name different things, or one is a change and the other is not.
 *
 * The statistics are the ones model evaluation uses: the bias (mean
 * difference), the root-mean-square difference, its centred part, and the
 * pattern correlation, all weighted by cell area. Pure.
 *
 * AYNI (2026-10-06, slice 2's last item): B can also be another globe (2 or
 * 4 globes on screen), read over the Statistics tab's region side by side
 * with A (`regionDifferences`); a ratio quantity on both sides can be read
 * as a % difference, (A − B) / B, with the change map's near-zero rule
 * (`ratioRefusal`, `compareFields` with `mode: 'percent'`); and the same
 * picture is drawn every frame on the graphics card (`compareLive.ts`).
 */
import { latLonToTexelUv, lonSpanDegrees } from '../datasetProbe'
import type { LumaSnapshot } from '../glLumaSampler'
import type { ColorScale } from '../../types/color-scale'
import type { DatasetOverlayOptions } from '../../types'
import type { LatLonBounds, RegionStats } from '../datasetStats'
import { niceLimit, percentFloor, percentRefusal, quantile, symmetricRange, validRange, valueTable, type ChangeGrid, type ChangeRange } from './changeMap'

const EARTH_RADIUS_KM = 6371.0088

// --- Units -----------------------------------------------------------------

/** B's value in A's units: `factor * b + offset`. */
export interface Affine {
  readonly factor: number
  readonly offset: number
}

type UnitDef = { family: string; factor: number; offset?: number }

/** Each unit's family and its value in the family's base unit (`factor * v + offset`). Aliases are normalised first. */
const UNITS: Record<string, UnitDef> = {
  // Temperature: base K.
  k: { family: 'temperature', factor: 1 },
  degc: { family: 'temperature', factor: 1, offset: 273.15 },
  degf: { family: 'temperature', factor: 5 / 9, offset: 273.15 - (32 * 5) / 9 },
  // Depth (rain, snow, sea level): base m.
  m: { family: 'length', factor: 1 },
  cm: { family: 'length', factor: 0.01 },
  mm: { family: 'length', factor: 0.001 },
  in: { family: 'length', factor: 0.0254 },
  ft: { family: 'length', factor: 0.3048 },
  // Rates of rain: base mm/s (1 kg m-2 s-1 of water is 1 mm/s).
  'mm/s': { family: 'rate', factor: 1 },
  'kg/m2/s': { family: 'rate', factor: 1 },
  'mm/h': { family: 'rate', factor: 1 / 3600 },
  'mm/6h': { family: 'rate', factor: 1 / 21600 },
  'mm/day': { family: 'rate', factor: 1 / 86400 },
  'in/h': { family: 'rate', factor: 25.4 / 3600 },
  'in/day': { family: 'rate', factor: 25.4 / 86400 },
  // Speed: base m/s.
  'm/s': { family: 'speed', factor: 1 },
  'km/h': { family: 'speed', factor: 1 / 3.6 },
  kt: { family: 'speed', factor: 1852 / 3600 },
  mph: { family: 'speed', factor: 0.44704 },
  // Pressure: base Pa.
  pa: { family: 'pressure', factor: 1 },
  hpa: { family: 'pressure', factor: 100 },
  kpa: { family: 'pressure', factor: 1000 },
  // Energy flux: base W/m2.
  'w/m2': { family: 'flux', factor: 1 },
  // Mixing ratios: base ppb.
  ppm: { family: 'mixing', factor: 1000 },
  ppb: { family: 'mixing', factor: 1 },
  // Mass concentration: base µg/m3.
  'ug/m3': { family: 'mass', factor: 1 },
  'mg/m3': { family: 'mass', factor: 1000 },
  // Fractions: base %.
  '%': { family: 'fraction', factor: 1 },
  fraction: { family: 'fraction', factor: 100 },
}

const ALIASES: Record<string, string> = {
  '°c': 'degc', 'ºc': 'degc', c: 'degc', celsius: 'degc', degreesc: 'degc', degrees_celsius: 'degc', degreecelsius: 'degc',
  '°f': 'degf', 'ºf': 'degf', fahrenheit: 'degf', degreesf: 'degf',
  kelvin: 'k',
  'kgm-2s-1': 'kg/m2/s', 'kg/m2s': 'kg/m2/s', 'kg/(m2s)': 'kg/m2/s',
  'mm/hr': 'mm/h', 'mmh-1': 'mm/h', 'mm/d': 'mm/day', 'mmday-1': 'mm/day', 'mmd-1': 'mm/day', 'mms-1': 'mm/s',
  'in/hr': 'in/h', inch: 'in', inches: 'in',
  'ms-1': 'm/s', 'kmh-1': 'km/h', 'kph': 'km/h', knots: 'kt', knot: 'kt', kn: 'kt',
  mbar: 'hpa', mb: 'hpa',
  'wm-2': 'w/m2', 'w*m-2': 'w/m2', 'w/m^2': 'w/m2',
  'μg/m3': 'ug/m3', 'µg/m3': 'ug/m3', 'ugm-3': 'ug/m3', 'mgm-3': 'mg/m3',
  percent: '%', pct: '%',
}

/** A unit's key in the table, or null when it is not in it. */
export function normaliseUnit(units: string | undefined | null): string | null {
  if (!units) return null
  const k = units.trim().toLowerCase()
    .replace(/²/g, '2').replace(/³/g, '3').replace(/[µμ]/g, 'u')
    .replace(/\s+/g, '').replace(/\^/g, '')
  const key = ALIASES[k] ?? k
  return key in UNITS ? key : null
}

/**
 * How to put B's values into A's units, or null when there is no known way
 * (different quantities, or a unit the table does not hold). `change`: either
 * dataset is a change or anomaly, so an offset (°C ↔ K ↔ °F) does not apply.
 */
export function unitConversion(fromB: string | undefined, toA: string | undefined, change = false): Affine | null {
  if ((fromB ?? '').trim() === (toA ?? '').trim() && (fromB ?? '').trim() !== '') return { factor: 1, offset: 0 }
  const b = normaliseUnit(fromB)
  const a = normaliseUnit(toA)
  if (!a || !b) return null
  const ub = UNITS[b]
  const ua = UNITS[a]
  if (ub.family !== ua.family) return null
  // value_A = (factorB * v + offsetB - offsetA) / factorA
  const factor = ub.factor / ua.factor
  const offset = change ? 0 : ((ub.offset ?? 0) - (ua.offset ?? 0)) / ua.factor
  return { factor, offset }
}

// --- Same quantity, same moment --------------------------------------------

/** A title names a change or an anomaly rather than a value. */
export function isChangeTitle(title: string): boolean {
  return /\b(change|anomal(y|ies)|departure|difference|vs\.?\s)/i.test(title)
}

const STOP = new Set(['forecast', 'real', 'time', 'historical', 'reanalysis', 'projected', 'observed', 'global', 'yearly', 'monthly', 'weekly', 'daily', 'hourly', 'running', 'months', 'year', 'next', 'days', 'with', 'from', 'the', 'and'])

/** The words a title's subject is made of: no kind prefix, no parenthesis, no scenario. */
export function subjectWords(title: string): Set<string> {
  const words = title
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\bSSP\d-\d\.\d\b/gi, ' ')
    .toLowerCase()
    .split(/[^a-z]+/)
    .filter((w) => w.length >= 4 && !STOP.has(w))
    .map((w) => w.replace(/(ies)$/, 'y').replace(/s$/, ''))
  return new Set(words)
}

/** Why two datasets may not be the same quantity, or null when nothing says so. */
export function quantityWarning(titleA: string, titleB: string): 'subject' | 'change' | null {
  if (isChangeTitle(titleA) !== isChangeTitle(titleB)) return 'change'
  const a = subjectWords(titleA)
  const b = subjectWords(titleB)
  if (!a.size || !b.size) return null
  for (const w of a) if (b.has(w)) return null
  return 'subject'
}

/**
 * Why a % difference cannot mean anything for one side, or null when it is a
 * ratio-scale quantity (zero means none: rain, a concentration, an optical
 * depth, an extent). Read from the encoding: refused when any value its codes
 * can carry is below zero (an encoding range may reach below zero for its
 * no-data codes, so the codes that carry data are what count), for a
 * temperature in °C or °F (an arbitrary zero, `percentRefusal`), for any
 * temperature (in kelvin a percentage is defined but nobody reads one:
 * differences are given in degrees), for a change or an anomaly (its zero is
 * "no change", not "none"), and for classes.
 */
export function ratioRefusal(scale: ColorScale, title: string): 'negative' | 'interval' | 'temperature' | 'change' | 'classified' | null {
  if ((scale as { release?: { kind?: string } }).release?.kind === 'luma8-classified') return 'classified'
  if (isChangeTitle(title)) return 'change'
  const table = valueTable(scale)
  let min = Infinity
  for (const v of table) if (Number.isFinite(v) && v < min) min = v
  // Float noise at the zero code is not a negative value.
  if (min < -1e-6 * Math.max(1, Math.abs(scale.vmax - scale.vmin))) return 'negative'
  if (percentRefusal({ vmin: 0, units: scale.units })) return 'interval'
  const unit = normaliseUnit(scale.units)
  if (unit && UNITS[unit].family === 'temperature') return 'temperature'
  return null
}

/** A frame's instant and the dataset's time step, ms. */
export interface FrameInstant {
  readonly ms: number
  readonly cadenceMs: number
}

/**
 * Whether two frames show the same moment: within half the coarser time
 * step. `unknown` when either has no dates (the comparison says so).
 */
export function pairFrames(a: FrameInstant | null, b: FrameInstant | null): { status: 'paired' | 'apart' | 'unknown'; gapMs: number | null } {
  if (!a || !b || !Number.isFinite(a.ms) || !Number.isFinite(b.ms)) return { status: 'unknown', gapMs: null }
  const gap = Math.abs(a.ms - b.ms)
  const tolerance = Math.max(a.cadenceMs, b.cadenceMs, 0) / 2
  return { status: gap <= tolerance ? 'paired' : 'apart', gapMs: gap }
}

/** How two frames are matched: by date, or as each globe shows them (the dates are stated, not checked). */
export type FramePairing = 'time' | 'shown'

/**
 * The frames to compare under `how`: by date as {@link pairFrames}, or
 * `shown`, which compares whatever is on screen and only reports the gap.
 */
export function pairFor(how: FramePairing, a: FrameInstant | null, b: FrameInstant | null): { status: 'paired' | 'apart' | 'unknown' | 'shown'; gapMs: number | null } {
  const pair = pairFrames(a, b)
  return how === 'shown' ? { status: 'shown', gapMs: pair.gapMs } : pair
}

// --- The difference ----------------------------------------------------------

export interface CompareField {
  readonly snapshot: LumaSnapshot
  readonly scale: ColorScale
  readonly options?: DatasetOverlayOptions
}

/** Degrees per texel (the coarser of its two directions). */
export function degreesPerTexel(field: CompareField): number {
  const bbox = field.options?.boundingBox
  const latSpan = bbox ? Math.abs(bbox.n - bbox.s) : 180
  return Math.max(lonSpanDegrees(field.options) / field.snapshot.width, latSpan / field.snapshot.height)
}

/** At most this many sub-samples per cell and direction when averaging the finer field down. */
const MAX_SUBSAMPLES = 8

/**
 * One field's mean in each cell of a global `width × height` grid: k × k
 * points per cell read through `latLonToTexelUv` (separable, as in
 * `computeChangeGrid`), averaged where at least half carry data; NaN
 * elsewhere. `k` follows how many of the field's texels a cell spans.
 */
export function cellMeans(field: CompareField, width: number, height: number): Float64Array {
  const { k, cols, rows } = cellIndex(field, width, height)
  const table = valueTable(field.scale)
  const { snapshot } = field
  const out = new Float64Array(width * height).fill(NaN)
  const need = (k * k) / 2
  for (let j = 0; j < height; j++) {
    for (let i = 0; i < width; i++) {
      let sum = 0
      let n = 0
      for (let sj = 0; sj < k; sj++) {
        const y = rows[j * k + sj]
        if (y < 0) continue
        const rowOffset = y * snapshot.width
        for (let si = 0; si < k; si++) {
          const x = cols[i * k + si]
          if (x < 0) continue
          const v = table[snapshot.data[rowOffset + x]]
          if (Number.isFinite(v)) {
            sum += v
            n++
          }
        }
      }
      if (n >= need && n > 0) out[j * width + i] = sum / n
    }
  }
  return out
}

/**
 * Where {@link cellMeans} reads: `k` sub-samples per cell and direction, and
 * for each sub-column and sub-row the field's texel column and row (−1
 * outside it). Shared with the live picture (`compareLive.ts`), so the
 * graphics card reads the very texels the paused picture averages.
 */
export function cellIndex(field: CompareField, width: number, height: number): { k: number; cols: Int32Array; rows: Int32Array } {
  const cellDeg = 360 / width
  const k = Math.max(1, Math.min(MAX_SUBSAMPLES, Math.ceil(cellDeg / degreesPerTexel(field) - 1e-9)))
  const { snapshot, options } = field
  const bbox = options?.boundingBox
  const midLat = bbox ? (bbox.n + bbox.s) / 2 : 0
  const midLon = bbox ? (bbox.w <= bbox.e ? (bbox.w + bbox.e) / 2 : ((bbox.w + bbox.e + 360) / 2 + 180) % 360 - 180) : 0
  const cols = new Int32Array(width * k).fill(-1)
  for (let i = 0; i < width * k; i++) {
    const uv = latLonToTexelUv(midLat, -180 + ((i + 0.5) * 360) / (width * k), options)
    if (uv) cols[i] = Math.min(snapshot.width - 1, Math.max(0, Math.floor(uv.u * snapshot.width)))
  }
  const rows = new Int32Array(height * k).fill(-1)
  for (let j = 0; j < height * k; j++) {
    const uv = latLonToTexelUv(90 - ((j + 0.5) * 180) / (height * k), midLon, options)
    if (uv) rows[j] = Math.min(snapshot.height - 1, Math.max(0, Math.floor(uv.v * snapshot.height)))
  }
  return { k, cols, rows }
}

export interface CompareStats {
  /** Cells with data in both, and their area. */
  readonly cells: number
  readonly areaKm2: number
  /** Share of the cells where either has data that both have. */
  readonly overlap: number
  /** Area-weighted mean of A − B. */
  readonly bias: number
  /** Area-weighted root-mean-square difference. */
  readonly rmsd: number
  /** The RMS difference once the bias is removed (Taylor 2001's centred RMS difference). */
  readonly centredRmsd: number
  /** Area-weighted Pearson correlation of A and B over the cells (the pattern correlation); NaN when either is flat. */
  readonly patternCorrelation: number
  readonly meanA: number
  readonly meanB: number
  /** The bias as a % of B's mean (for a ratio quantity); NaN when B's mean is zero. */
  readonly relativeBias: number
}

export interface CompareResult {
  /** The difference grid in `ChangeGrid`'s shape, so the change map's painter and legend draw it. */
  readonly grid: ChangeGrid
  readonly stats: CompareStats | null
  /** Degrees per cell of the common grid. */
  readonly cellDegrees: number
  /** Below this a difference is rounding in the data (the two steps added); null where steps are uneven (a log scale). */
  readonly resolution: number | null
}

/** The width of the common grid: the coarser dataset's resolution, at most 2048 cells round. */
export function commonGridWidth(a: CompareField, b: CompareField, maxWidth = 2048): number {
  const deg = Math.max(degreesPerTexel(a), degreesPerTexel(b), 360 / maxWidth)
  return Math.max(36, Math.min(maxWidth, Math.round(360 / deg)))
}

/** One quantisation step of a linear scale; null for a log or classified one. */
export function linearStep(scale: ColorScale): number | null {
  const release = (scale as { release?: { kind?: string } }).release
  if (release?.kind && release.kind !== 'luma8-linear') return null
  const step = Math.abs(scale.vmax - scale.vmin) / 255
  return step > 0 ? step : null
}

/** What {@link compareFields} draws and over where it counts. */
export interface CompareOptions {
  /** The colour range (± limit); else the 98th percentile of the cells' magnitudes, rounded 1-2-2.5-5. */
  readonly limit?: number | null
  /** AYNI (2026-10-20): the viewer's own range (min < 0 < max); null for ± limit. */
  readonly range?: ChangeRange | null
  /** `percent`: (A − B) / |B| × 100, grey where |B| is under `floor`. Only for ratio quantities ({@link ratioRefusal}). */
  readonly mode?: 'difference' | 'percent'
  /** % mode: the |B| under which no percentage is drawn; else `percentFloor` over B's cells. */
  readonly floor?: number | null
  /** The statistics over this box only; the map always covers everything. */
  readonly region?: LatLonBounds | null
}

/** Whether a place lies in a box (one crossing the antimeridian has w > e). */
export function inBounds(lat: number, lon: number, b: LatLonBounds): boolean {
  if (lat > b.n || lat < b.s) return false
  return b.w <= b.e ? lon >= b.w && lon <= b.e : lon >= b.w || lon <= b.e
}

/**
 * A − B on the common grid, B in A's units through `convertB`. Grey where
 * one has data and the other not (and, as a %, where B is within the floor
 * of zero); transparent where neither has. The statistics are always in A's
 * units, over `region` when one is given.
 */
export function compareFields(a: CompareField, b: CompareField, convertB: Affine, opts: CompareOptions = {}): CompareResult {
  const width = commonGridWidth(a, b)
  const height = Math.max(18, Math.round(width / 2))
  const ma = cellMeans(a, width, height)
  const mb = cellMeans(b, width, height)
  const mode = opts.mode ?? 'difference'
  const stepA = linearStep(a.scale)
  const stepB = linearStep(b.scale)
  const stepBinA = stepB !== null ? Math.abs(convertB.factor) * stepB : null
  // % mode: the change map's rule, two of B's steps or 2 % of its typical magnitude.
  let floor: number | null = null
  if (mode === 'percent') {
    if (opts.floor !== undefined && opts.floor !== null) floor = opts.floor
    else {
      const magnitudes: number[] = []
      for (let k = 0; k < mb.length; k++) if (Number.isFinite(mb[k])) magnitudes.push(Math.abs(convertB.factor * mb[k] + convertB.offset))
      floor = percentFloor(magnitudes, stepBinA)
    }
  }
  const values = new Float32Array(width * height).fill(NaN)
  const grey = new Uint8Array(width * height)
  let painted = 0
  let masked = 0
  let negative = 0
  let either = 0
  let bothCells = 0
  // Weighted sums for the statistics.
  let sw = 0, sd = 0, sd2 = 0, sa = 0, sb = 0, saa = 0, sbb = 0, sab = 0, area = 0
  const cellRad = (Math.PI / 180) * (360 / width)
  const region = opts.region ?? null
  for (let j = 0; j < height; j++) {
    const lat1 = 90 - (j * 180) / height
    const lat2 = 90 - ((j + 1) * 180) / height
    const w = Math.sin((lat1 * Math.PI) / 180) - Math.sin((lat2 * Math.PI) / 180)
    const latC = 90 - ((j + 0.5) * 180) / height
    for (let i = 0; i < width; i++) {
      const k = j * width + i
      const va = ma[k]
      const vb = Number.isFinite(mb[k]) ? convertB.factor * mb[k] + convertB.offset : NaN
      const fa = Number.isFinite(va)
      const fb = Number.isFinite(vb)
      if (!fa && !fb) continue
      const counted = !region || inBounds(latC, -180 + ((i + 0.5) * 360) / width, region)
      if (counted) either++
      if (fa !== fb) {
        grey[k] = 1
        continue
      }
      const d = va - vb
      if (mode === 'percent') {
        if (vb < 0) negative++
        if (Math.abs(vb) < (floor ?? 0) || vb === 0) {
          grey[k] = 1
          masked++
        } else {
          values[k] = (100 * d) / Math.abs(vb)
          painted++
        }
      } else {
        values[k] = d
        painted++
      }
      if (!counted) continue
      bothCells++
      sw += w
      sd += w * d
      sd2 += w * d * d
      sa += w * va
      sb += w * vb
      saa += w * va * va
      sbb += w * vb * vb
      sab += w * va * vb
      area += w * cellRad * EARTH_RADIUS_KM * EARTH_RADIUS_KM
    }
  }
  let stats: CompareStats | null = null
  if (sw > 0) {
    const bias = sd / sw
    const rmsd = Math.sqrt(sd2 / sw)
    const meanA = sa / sw
    const meanB = sb / sw
    const varA = saa / sw - meanA * meanA
    const varB = sbb / sw - meanB * meanB
    const cov = sab / sw - meanA * meanB
    const r = varA > 1e-12 && varB > 1e-12 ? cov / Math.sqrt(varA * varB) : NaN
    stats = {
      cells: bothCells,
      areaKm2: area,
      overlap: either > 0 ? bothCells / either : 0,
      bias,
      rmsd,
      centredRmsd: Math.sqrt(Math.max(0, rmsd * rmsd - bias * bias)),
      patternCorrelation: Number.isFinite(r) ? Math.max(-1, Math.min(1, r)) : NaN,
      meanA,
      meanB,
      relativeBias: meanB !== 0 ? (100 * bias) / Math.abs(meanB) : NaN,
    }
  }
  const magnitudes = new Float32Array(values.length)
  for (let k = 0; k < values.length; k++) magnitudes[k] = Math.abs(values[k])
  const limit = opts.limit
  const fixed = limit !== undefined && limit !== null && limit > 0 ? limit : niceLimit(quantile(magnitudes, 0.98))
  return {
    grid: { width, height, values, grey, mode, limit: fixed, range: validRange(opts.range) ? opts.range : symmetricRange(fixed), masked, floor, painted, negative },
    stats,
    cellDegrees: 360 / width,
    resolution: stepA !== null && stepBinA !== null ? stepA + stepBinA : null,
  }
}

// --- One region on two globes, side by side ---------------------------------

/** A statistic on each side and their difference, all in A's units. */
export interface RegionDifference {
  readonly key: 'mean' | 'median' | 'p10' | 'p90'
  readonly a: number
  readonly b: number
  readonly difference: number
  /** (A − B) / |B| × 100 for a ratio quantity; null otherwise or where B is zero. */
  readonly percent: number | null
}

/**
 * The region's area-weighted mean, median and 10th and 90th percentiles on
 * each globe, B in A's units, with A − B (and, for a ratio quantity, the
 * difference as a % of B). Each side over its own texels with data at its
 * own resolution, so the two may cover slightly different places; the
 * model-evaluation statistics are the ones on a common grid.
 */
export function regionDifferences(a: RegionStats, b: RegionStats, convertB: Affine, ratio: boolean): RegionDifference[] {
  const keys = ['mean', 'median', 'p10', 'p90'] as const
  return keys.map((key) => {
    const va = a[key]
    // A positive factor keeps the order, so B's percentiles convert value by value.
    const vb = convertB.factor * b[key] + convertB.offset
    const difference = va - vb
    return { key, a: va, b: vb, difference, percent: ratio && vb !== 0 ? (100 * difference) / Math.abs(vb) : null }
  })
}
